import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MaterialModule } from 'src/app/material/material.module';
import { ShowDialogsComponent } from 'src/app/components/show-dialogs/show-dialogs.component';
import { SharedFeaturesService } from 'src/app/services/shared-features.service';
import {
  DocumentationService,
  VersionDoc,
} from 'src/app/services/documentation.service';
import { GuideEditorDialogComponent } from './guide-editor-dialog.component';

const ALLOWED_EXTENSIONS = ['pdf', 'xlsx', 'docx'];
const MAX_FILE_SIZE = 32 * 1024 * 1024; // 32 MB

/**
 * Producer "Documentation" panel of one solution version (mockup C):
 * write/edit markdown guides in-app, upload downloadable documents
 * (PDF / XLSX / DOCX), scope each doc to all subscribers or selected tenants.
 */
@Component({
  selector: 'app-version-documentation',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule],
  template: `
    <div class="vdoc">
      <div class="vdoc-h">
        <span>Documentation</span>
        <button class="btn-primary sm" type="button" (click)="openEditor()">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
          </svg>
          Write guide
        </button>
      </div>

      <div class="vdoc-b">
        <div
          class="drop"
          [class.dragging]="dragging"
          (dragover)="onDragOver($event)"
          (dragleave)="dragging = false"
          (drop)="onDrop($event)"
        >
          <span class="cloud">
            <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor"
                 stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <path d="M16 16l-4-4-4 4" />
              <path d="M12 12v9" />
              <path d="M20.4 18.6A5 5 0 0 0 18 9h-1.3A8 8 0 1 0 4 16.9" />
            </svg>
          </span>
          <div class="txtwrap">
            <div class="big">
              Drag &amp; drop documents here, or
              <span class="browse" (click)="fileInput.click()">browse</span>
            </div>
            <div class="hint">
              PDF / XLSX / DOCX listed as downloadable documents · 32 MB each ·
              guides are written in-app via “Write guide”
            </div>
          </div>

          <span class="audsel">
            <span class="audsel-lbl">Visible to:</span>
            <mat-form-field
              class="audfield"
              appearance="outline"
              subscriptSizing="dynamic"
            >
              <mat-select
                multiple
                [(ngModel)]="uploadAudience"
                placeholder="All subscribers"
              >
                <mat-select-trigger>{{ audienceLabel(uploadAudience) }}</mat-select-trigger>
                <mat-option *ngFor="let tenant of tenantOptions" [value]="tenant">
                  {{ tenant }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </span>

          <button
            class="btn-primary"
            type="button"
            [disabled]="uploading"
            (click)="fileInput.click()"
          >
            {{ uploading ? 'Uploading…' : 'Browse files' }}
          </button>
          <input
            #fileInput
            type="file"
            hidden
            multiple
            accept=".pdf,.xlsx,.docx"
            (change)="onFilesPicked($event)"
          />
        </div>
      </div>

      <div class="vdoc-table" *ngIf="docs.length">
        <table>
          <thead>
            <tr>
              <th>Document</th>
              <th>Type</th>
              <th>Audience</th>
              <th>Size</th>
              <th>Uploaded</th>
              <th class="ta-r">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let doc of docs">
              <td>
                <span class="fname">
                  <span class="pdfico" [ngClass]="iconClass(doc)">{{ iconLabel(doc) }}</span>
                  {{ doc.title }}
                </span>
              </td>
              <td>
                <span class="kind">{{ isGuide(doc) ? 'Guide' : 'Document' }}</span>
              </td>
              <td>
                <span class="aud" [class.tenant]="doc.tenant_names?.length">
                  {{ audienceLabel(doc.tenant_names) }}
                </span>
              </td>
              <td>{{ formatSize(doc.size) }}</td>
              <td>{{ doc.updated_dtm | date : 'MMM d, y' }}</td>
              <td>
                <span class="row-actions">
                  <span
                    *ngIf="isGuide(doc)"
                    class="iconbtn"
                    matTooltip="Edit guide"
                    (click)="openEditor(doc)"
                  >
                    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                         stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                  </span>
                  <span
                    *ngIf="!isGuide(doc)"
                    class="iconbtn dl"
                    matTooltip="Download"
                    (click)="download(doc)"
                  >
                    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                         stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M12 3v12M7 10l5 5 5-5" />
                      <path d="M5 21h14" />
                    </svg>
                  </span>
                  <span class="iconbtn danger" matTooltip="Delete" (click)="confirmDelete(doc)">
                    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                         stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M3 6h18" />
                      <path d="M8 6V4h8v2" />
                      <path d="M6 6l1 14h10l1-14" />
                    </svg>
                  </span>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="vdoc-empty" *ngIf="!loading && !docs.length">
        No documentation yet — write a guide or drop a document above.
        Subscribers see these on their Documentation page.
      </div>
    </div>
  `,
  styleUrls: ['./version-documentation.component.scss'],
})
export class VersionDocumentationComponent implements OnInit, OnChanges {
  @Input() solutionId: string | undefined | null;
  @Input() versionId: string | undefined | null;
  /** Optional labels used only for the guide-editor dialog subtitle. */
  @Input() solutionName: string | undefined | null;
  @Input() versionName: string | undefined | null;

  docs: VersionDoc[] = [];
  loading = false;
  uploading = false;
  dragging = false;

  /** Selected audience tenants; empty = all subscribers. */
  uploadAudience: string[] = [];
  /** Tenants subscribed to this solution (from the backend). */
  subscriberTenants: string[] = [];

  constructor(
    private docService: DocumentationService,
    private snackBar: SharedFeaturesService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadDocs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['solutionId'] || changes['versionId']) {
      this.loadDocs();
    }
  }

  /** Subscriber tenants plus any tenant already used by this version's docs. */
  get tenantOptions(): string[] {
    const used = this.docs.flatMap((doc) => doc.tenant_names || []);
    return Array.from(new Set([...this.subscriberTenants, ...used])).sort();
  }

  audienceLabel(tenants: string[] | null | undefined): string {
    return tenants && tenants.length ? tenants.join(', ') + ' only' : 'All subscribers';
  }

  isGuide(doc: VersionDoc): boolean {
    return doc.doc_type === 'guide';
  }

  iconLabel(doc: VersionDoc): string {
    if (this.isGuide(doc)) return 'MD';
    const ext = (doc.file_name || '').split('.').pop()?.toLowerCase();
    if (ext === 'xlsx') return 'XLSX';
    if (ext === 'docx') return 'DOCX';
    return 'PDF';
  }

  iconClass(doc: VersionDoc): string {
    if (this.isGuide(doc)) return 'md';
    const ext = (doc.file_name || '').split('.').pop()?.toLowerCase();
    if (ext === 'xlsx') return 'xls';
    if (ext === 'docx') return 'docx';
    return '';
  }

  formatSize(size: number | null | undefined): string {
    if (!size) return '—';
    if (size >= 1024 * 1024) return (size / (1024 * 1024)).toFixed(1) + ' MB';
    if (size >= 1024) return Math.round(size / 1024) + ' KB';
    return size + ' B';
  }

  loadDocs(): void {
    if (!this.solutionId || !this.versionId) return;
    this.loading = true;
    this.docService.getVersionDocs(this.solutionId, this.versionId).subscribe({
      next: (response: any) => {
        const docs: VersionDoc[] = response?.msg || [];
        this.subscriberTenants = response?.subscriber_tenants || [];
        // Guides first (as in the mockup), each group alphabetical.
        this.docs = docs.sort((a, b) => {
          const guideOrder = Number(!this.isGuide(a)) - Number(!this.isGuide(b));
          if (guideOrder !== 0) return guideOrder;
          return (a.title || '').localeCompare(b.title || '');
        });
        this.loading = false;
      },
      error: async (error) => {
        this.loading = false;
        this.snackBar.openSnackBar(
          await this.docService.parseError(error, 'Unable to load documentation'),
          'Close',
          'error-snackbar'
        );
      },
    });
  }

  private get selectedTenants(): string[] | null {
    return this.uploadAudience.length ? this.uploadAudience : null;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragging = true;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragging = false;
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length) this.uploadFiles(files);
  }

  onFilesPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';
    if (files.length) this.uploadFiles(files);
  }

  private uploadFiles(files: File[]): void {
    if (!this.solutionId || !this.versionId) return;

    const rejected: string[] = [];
    const accepted = files.filter((file) => {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        rejected.push(`${file.name} — only PDF, XLSX and DOCX are allowed`);
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        rejected.push(`${file.name} — larger than 32 MB`);
        return false;
      }
      return true;
    });

    if (rejected.length) {
      this.snackBar.openSnackBar(
        `Skipped: ${rejected.join('; ')}`,
        'Close',
        'error-snackbar'
      );
    }
    if (!accepted.length) return;

    this.uploading = true;
    this.docService
      .uploadVersionDocs(
        this.solutionId,
        this.versionId,
        accepted,
        this.selectedTenants
      )
      .subscribe({
        next: () => {
          this.uploading = false;
          this.snackBar.openSnackBar(
            `${accepted.length} document${accepted.length > 1 ? 's' : ''} uploaded`,
            'Close',
            'success-snackbar'
          );
          this.loadDocs();
        },
        error: async (error) => {
          this.uploading = false;
          this.snackBar.openSnackBar(
            await this.docService.parseError(error, 'Upload failed'),
            'Close',
            'error-snackbar'
          );
        },
      });
  }

  openEditor(doc?: VersionDoc): void {
    if (!this.solutionId || !this.versionId) return;
    const editorRef = this.dialog.open(GuideEditorDialogComponent, {
      data: {
        solutionId: this.solutionId,
        versionId: this.versionId,
        solutionName: this.solutionName,
        versionName: this.versionName,
        tenants: this.tenantOptions,
        doc: doc,
      },
      width: '96vw',
      maxWidth: '1160px',
      panelClass: 'guide-editor-dialog',
      disableClose: true,
    });
    editorRef.afterClosed().subscribe((saved) => {
      if (saved) this.loadDocs();
    });
  }

  download(doc: VersionDoc): void {
    if (!doc.url) return;
    this.docService
      .downloadDoc(doc.url, doc.file_name || doc.title)
      .subscribe({
        error: async (error) => {
          this.snackBar.openSnackBar(
            await this.docService.parseError(error, 'Download failed'),
            'Close',
            'error-snackbar'
          );
        },
      });
  }

  confirmDelete(doc: VersionDoc): void {
    const dialogRef = this.dialog.open(ShowDialogsComponent, {
      data: {
        title: this.isGuide(doc) ? 'Delete guide' : 'Delete document',
        message: `Delete '${doc.title}'? Subscribers will no longer see it.`,
        confirmText: 'Delete',
        danger: true,
      },
      disableClose: false,
    });
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed || !this.solutionId || !this.versionId) return;
      this.docService
        .deleteVersionDoc(this.solutionId, this.versionId, doc.id)
        .subscribe({
          next: () => {
            this.snackBar.openSnackBar(
              `'${doc.title}' deleted`,
              'Close',
              'success-snackbar'
            );
            this.loadDocs();
          },
          error: async (error) => {
            this.snackBar.openSnackBar(
              await this.docService.parseError(error, 'Delete failed'),
              'Close',
              'error-snackbar'
            );
          },
        });
    });
  }
}
